import { forwardRef, type FormEvent, type ReactNode } from "react";
import { Alert, Button, Group, Modal, ScrollArea, Text, type ModalProps, type ScrollAreaAutosizeProps } from "@mantine/core";
import { IconLock, IconPencil } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import classes from "./EntityModal.module.css";

/**
 * Mantine's modal scroll area with a thinner, quieter scrollbar that appears
 * while scrolling rather than whenever the pointer is over the dialog — which,
 * in a dialog, is always.
 */
const DialogScrollArea = forwardRef<HTMLDivElement, ScrollAreaAutosizeProps>((props, ref) => (
    <ScrollArea.Autosize
        ref={ref}
        {...props}
        type="scroll"
        scrollbarSize={8}
        classNames={{ thumb: classes.thumb }}
    />
));
DialogScrollArea.displayName = "DialogScrollArea";

interface Props {
    opened: boolean;
    onClose: () => void;
    /** "Ereignis" for an existing record, "Neues Ereignis" for a new one. */
    title: string;
    /** The record's code, shown next to the title. */
    code?: string | null;
    /** Usually the record's status badge. */
    badge?: ReactNode;
    size?: ModalProps["size"];
    readOnly: boolean;
    /** Says that the record is closed, above the fields. */
    closed?: boolean;
    /** Offered in read-only mode when the user may switch to editing. */
    onUnlock?: () => void;
    onSubmit: (event?: FormEvent<HTMLFormElement>) => void;
    saving?: boolean;
    submitLabel: string;
    /** Extra actions at the start of the footer, e.g. the history. */
    footerStart?: ReactNode;
    children: ReactNode;
}

/**
 * The frame every record dialog shares: title with code and status, a body
 * that scrolls on its own, and a footer that stays in view — Cancel/Save while
 * editing, Close (and Bearbeiten, where allowed) while read-only.
 */
export function EntityModal({
    opened, onClose, title, code, badge, size = "lg", readOnly, closed, onUnlock,
    onSubmit, saving, submitLabel, footerStart, children,
}: Props) {
    const { t } = useTranslation();
    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size={size}
            scrollAreaComponent={DialogScrollArea as typeof ScrollArea.Autosize}
            title={
                <Group gap="sm" wrap="nowrap">
                    <Text fw={600}>{title}</Text>
                    {code && <Text ff="monospace" size="sm" c="dimmed">{code}</Text>}
                    {badge}
                </Group>
            }
        >
            <form
                // Enter in a field submits a form; a read-only one has nothing to submit.
                onSubmit={readOnly ? (e) => e.preventDefault() : onSubmit}
                className={readOnly ? `${classes.form} ${classes.readOnly}` : classes.form}
                // Mantine's focus trap otherwise focuses the first tabbable
                // element — the header's close button, ringed as if it were
                // the thing to press. Focusing the content instead puts Tab's
                // next stop on the first field.
                tabIndex={-1}
                data-autofocus
            >
                {closed && readOnly && (
                    <Alert variant="light" color="gray" icon={<IconLock size={16} />} mb="md" py="xs">
                        {onUnlock ? t("common.dialog.closedNoticeUnlock") : t("common.dialog.closedNotice")}
                    </Alert>
                )}
                {children}
                <div className={classes.footer}>
                    {footerStart && <div className={classes.footerStart}>{footerStart}</div>}
                    {readOnly ? (
                        <>
                            {onUnlock && (
                                <Button variant="default" leftSection={<IconPencil size={16} />} onClick={onUnlock}>
                                    {t("common.action.edit")}
                                </Button>
                            )}
                            <Button onClick={onClose}>{t("common.action.close")}</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="default" onClick={onClose}>{t("common.action.cancel")}</Button>
                            <Button type="submit" loading={saving}>{submitLabel}</Button>
                        </>
                    )}
                </div>
            </form>
        </Modal>
    );
}
